<template>
  <div class="proxy-download-logs">
    <!-- 顶部操作栏 -->
    <div class="operation-bar">
      <el-row :gutter="20">
        <el-col :span="5">
          <el-input
            v-model="searchForm.username"
            placeholder="请输入用户名"
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch"
          >
            <i slot="prefix" class="el-input__icon el-icon-user"></i>
          </el-input>
        </el-col>
        <el-col :span="5">
          <el-input
            v-model="searchForm.ip"
            placeholder="请输入IP地址"
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch"
          >
            <i slot="prefix" class="el-input__icon el-icon-location"></i>
          </el-input>
        </el-col>
        <el-col :span="5">
          <el-select
            v-model="searchForm.status"
            placeholder="下载状态"
            clearable
            @clear="handleSearch"
            @change="handleSearch"
            style="width: 100%"
          >
            <el-option label="已开始" value="started"></el-option>
            <el-option label="已完成" value="completed"></el-option>
            <el-option label="失败" value="failed"></el-option>
            <el-option label="中止" value="aborted"></el-option>
          </el-select>
        </el-col>
        <el-col :span="9">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            @change="handleDateChange"
            style="width: 100%"
          >
          </el-date-picker>
        </el-col>
      </el-row>
      <el-row :gutter="20" style="margin-top: 15px">
        <el-col :span="24" style="text-align: right">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">
            搜索
          </el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">
            重置
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 下载日志列表 -->
    <div class="table-container">
      <el-table :data="logList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="60"></el-table-column>
        <el-table-column prop="username" label="用户名" width="120"></el-table-column>
        <el-table-column prop="file_name" label="文件名" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="ip_address" label="IP地址" width="140"></el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)" size="small">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="file_size" label="文件大小" width="110">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.file_size) }}
          </template>
        </el-table-column>
        <el-table-column prop="downloaded_bytes" label="已下载" width="110">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.downloaded_bytes) }}
          </template>
        </el-table-column>
        <el-table-column prop="progress" label="进度" width="120">
          <template slot-scope="scope">
            <el-progress 
              :percentage="calculateProgress(scope.row)" 
              :status="getProgressStatus(scope.row.status)"
              :stroke-width="8"
            ></el-progress>
          </template>
        </el-table-column>
        <el-table-column prop="duration_seconds" label="耗时" width="90">
          <template slot-scope="scope">
            {{ formatDuration(scope.row.duration_seconds) }}
          </template>
        </el-table-column>
        <el-table-column prop="start_time" label="开始时间" width="160">
          <template slot-scope="scope">
            {{ formatDate(scope.row.start_time) }}
          </template>
        </el-table-column>
        <el-table-column prop="end_time" label="结束时间" width="160">
          <template slot-scope="scope">
            {{ formatDate(scope.row.end_time) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.error_message"
              type="text"
              size="small"
              @click="showErrorDetail(scope.row)"
            >
              查看错误
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        >
        </el-pagination>
      </div>
    </div>

    <!-- 错误详情弹窗 -->
    <el-dialog
      title="错误详情"
      :visible.sync="errorDialogVisible"
      width="600px"
    >
      <div class="error-detail">
        <el-alert
          :title="`文件：${currentError.file_name}`"
          type="error"
          :closable="false"
          show-icon
        >
        </el-alert>
        <div class="error-info">
          <p><strong>用户：</strong>{{ currentError.username }}</p>
          <p><strong>IP地址：</strong>{{ currentError.ip_address }}</p>
          <p><strong>时间：</strong>{{ formatDate(currentError.start_time) }}</p>
        </div>
        <el-divider></el-divider>
        <div class="error-message">
          <h4>错误信息：</h4>
          <pre>{{ currentError.error_message }}</pre>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="errorDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getProxyDownloadLogs } from "../api/index";

export default {
  name: "ProxyDownloadLogs",
  data() {
    return {
      loading: false,
      logList: [],
      dateRange: [],
      searchForm: {
        username: "",
        ip: "",
        status: "",
      },
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
      },
      errorDialogVisible: false,
      currentError: {
        file_name: "",
        username: "",
        ip_address: "",
        start_time: "",
        error_message: "",
      },
    };
  },
  created() {
    this.fetchLogList();
  },
  methods: {
    // 获取日志列表
    async fetchLogList() {
      try {
        this.loading = true;
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.pageSize,
          ...this.searchForm,
        };

        // 添加日期范围参数
        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = this.dateRange[0];
          params.endDate = this.dateRange[1];
        }

        const res = await getProxyDownloadLogs(params);
        this.logList = res.data.list;
        this.pagination.total = res.data.pagination.total;
      } catch (error) {
        console.error(error);
        this.$message.error("获取下载日志失败");
      } finally {
        this.loading = false;
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.fetchLogList();
    },

    // 重置
    handleReset() {
      this.searchForm = {
        username: "",
        ip: "",
        status: "",
      };
      this.dateRange = [];
      this.pagination.currentPage = 1;
      this.fetchLogList();
    },

    // 日期范围改变
    handleDateChange() {
      this.handleSearch();
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.fetchLogList();
    },

    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.fetchLogList();
    },

    // 获取状态类型
    getStatusType(status) {
      const statusMap = {
        started: "info",
        completed: "success",
        failed: "danger",
        aborted: "warning",
      };
      return statusMap[status] || "info";
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        started: "进行中",
        completed: "已完成",
        failed: "失败",
        aborted: "中止",
      };
      return statusMap[status] || status;
    },

    // 获取进度条状态
    getProgressStatus(status) {
      if (status === 'completed') return 'success';
      if (status === 'failed') return 'exception';
      return null;
    },

    // 计算进度
    calculateProgress(row) {
      if (!row.file_size || row.file_size === 0) return 0;
      const progress = (row.downloaded_bytes / row.file_size) * 100;
      return Math.min(Math.round(progress), 100);
    },

    // 格式化文件大小
    formatFileSize(size) {
      if (!size || size === 0) return "0 B";
      const units = ["B", "KB", "MB", "GB", "TB"];
      let index = 0;
      let fileSize = size;
      while (fileSize >= 1024 && index < units.length - 1) {
        fileSize /= 1024;
        index++;
      }
      return `${fileSize.toFixed(2)} ${units[index]}`;
    },

    // 格式化时长
    formatDuration(seconds) {
      if (!seconds) return "-";
      if (seconds < 60) return `${seconds}秒`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes < 60) {
        return `${minutes}分${remainingSeconds}秒`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}时${remainingMinutes}分${remainingSeconds}秒`;
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")} ${String(
        d.getHours()
      ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(
        d.getSeconds()
      ).padStart(2, "0")}`;
    },

    // 显示错误详情
    showErrorDetail(row) {
      this.currentError = {
        file_name: row.file_name,
        username: row.username,
        ip_address: row.ip_address,
        start_time: row.start_time,
        error_message: row.error_message,
      };
      this.errorDialogVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.proxy-download-logs {
  padding: 20px;

  .operation-bar {
    background-color: #fff;
    padding: 20px;
    margin-bottom: 16px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .table-container {
    background-color: #fff;
    padding: 16px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .pagination-container {
    margin-top: 20px;
    text-align: right;
  }

  .error-detail {
    .error-info {
      margin-top: 15px;
      p {
        margin: 8px 0;
        color: #606266;
      }
    }

    .error-message {
      h4 {
        margin-bottom: 10px;
        color: #303133;
      }

      pre {
        background-color: #f5f7fa;
        padding: 15px;
        border-radius: 4px;
        border-left: 3px solid #f56c6c;
        color: #f56c6c;
        font-size: 13px;
        line-height: 1.6;
        overflow-x: auto;
        white-space: pre-wrap;
        word-wrap: break-word;
      }
    }
  }
}
</style>
